import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtPct, exportToPDF, exportToExcel } from "../lib/exports";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function ReporteMensual() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/mes", { params: { year, month } });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const expPDF = () => {
    if (!data) return;
    exportToPDF({
      title: `Reporte ${MONTHS[month - 1]} ${year}`, subtitle: `${data.fecha_ini} a ${data.fecha_fin}`,
      columns: [
        { header: "Sucursal", key: "sucursal" }, { header: "Ventas", key: "ventas_fmt" },
        { header: "Costo", key: "costo_fmt" }, { header: "Utilidad Neta", key: "util_fmt" }, { header: "Margen", key: "margen_fmt" },
      ],
      rows: data.por_sucursal.map((s) => ({ ...s, ventas_fmt: fmtMoney(s.ventas), costo_fmt: fmtMoney(s.costo_venta), util_fmt: fmtMoney(s.utilidad_neta), margen_fmt: fmtPct(s.margen_pct) })),
      summary: [
        { label: "Total Ventas", value: fmtMoney(data.resumen.ventas) },
        { label: "Utilidad Neta", value: fmtMoney(data.resumen.utilidad_neta) },
        { label: "Margen", value: fmtPct(data.resumen.margen_pct) },
      ],
    });
  };
  const expXLS = () => {
    if (!data) return;
    exportToExcel({ title: `Reporte_${MONTHS[month - 1]}_${year}`, rows: data.por_sucursal.map((s) => ({
      Sucursal: s.sucursal, Ventas: s.ventas, Costo: s.costo_venta, "Gastos Op.": s.gastos_operativos,
      "Utilidad Neta": s.utilidad_neta, Margen: s.margen_pct, "Food Cost %": s.food_cost_pct,
    })) });
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Reportes</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Reporte Mensual</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={expPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
          <Button variant="outline" onClick={expXLS}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <Card className="p-5 grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mes</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end"><Button onClick={cargar} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Consultar</Button></div>
      </Card>

      {loading && <div className="text-sm text-slate-500">Cargando…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="Ventas Totales" value={fmtMoney(data.resumen.ventas)} />
            <KPI label="Costos Totales" value={fmtMoney(data.resumen.costo_venta)} />
            <KPI label="Gastos Operativos" value={fmtMoney(data.resumen.gastos_operativos)} />
            <KPI label="Utilidad Neta" value={fmtMoney(data.resumen.utilidad_neta)} positive={data.resumen.utilidad_neta >= 0} />
          </div>

          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Comparativo entre sucursales</div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.por_sucursal}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="sucursal" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "#1e293b", border: "none", borderRadius: 6, color: "white" }} />
                  <Legend />
                  <Bar dataKey="ventas" fill="#2563eb" name="Ventas" />
                  <Bar dataKey="utilidad_neta" fill="#10b981" name="Utilidad Neta" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr><Th>Sucursal</Th><Th right>Ventas</Th><Th right>Costo</Th><Th right>Gastos Op.</Th><Th right>Utilidad Neta</Th><Th right>Margen</Th><Th right>Food Cost</Th></tr>
                </thead>
                <tbody>
                  {data.por_sucursal.map((s) => (
                    <tr key={s.sucursal_id} className="border-b border-slate-100">
                      <Td>{s.sucursal}</Td>
                      <Td right mono>{fmtMoney(s.ventas)}</Td>
                      <Td right mono>{fmtMoney(s.costo_venta)}</Td>
                      <Td right mono>{fmtMoney(s.gastos_operativos)}</Td>
                      <Td right mono className={s.utilidad_neta >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>{fmtMoney(s.utilidad_neta)}</Td>
                      <Td right mono>{fmtPct(s.margen_pct)}</Td>
                      <Td right mono>{fmtPct(s.food_cost_pct)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-emerald-600 mb-3">Top productos vendidos</div>
              <ProductTable items={data.top_productos} />
            </Card>
            <Card className="p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-red-600 mb-3">Productos con menor margen</div>
              <ProductTable items={data.menor_margen} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

const KPI = ({ label, value, positive }) => (
  <Card className="p-4">
    <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
    <div className={`mt-2 text-2xl font-bold font-mono ${positive === true ? "text-emerald-600" : positive === false ? "text-red-600" : ""}`}>{value}</div>
  </Card>
);
const ProductTable = ({ items }) => (
  <table className="w-full text-sm">
    <thead className="border-b border-slate-200"><tr><th className="px-2 py-2 text-left">Producto</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">Margen</th></tr></thead>
    <tbody>
      {items.length === 0 && <tr><td colSpan={3} className="px-2 py-4 text-center text-slate-400">Sin datos</td></tr>}
      {items.map((p) => (
        <tr key={p.producto_id} className="border-b border-slate-100">
          <td className="px-2 py-1.5">{p.nombre}</td>
          <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(p.total)}</td>
          <td className={`px-2 py-1.5 text-right font-mono ${p.margen_pct >= 55 ? "text-emerald-600" : p.margen_pct >= 45 ? "text-amber-600" : "text-red-600"}`}>{fmtPct(p.margen_pct)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
const Th = ({ children, right }) => <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`px-4 py-2.5 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
